import { NavLink, useNavigate } from 'react-router-dom';
import './navigation.css';
import { companyName } from './configs';
import { useCompany } from './contexts/CompanyContext';
import { useCrmSettings } from './CRM/SimpleCRM/CrmSettingsContext';
import Cookies from 'js-cookie';
import { useUnreadNotifications } from './utils/useUnreadNotifications'; // [VP-NOTIF-BADGE 2026-05-28]

// Static nav links (non-CRM)
const BASE_NAV_LINKS = [
  { pageId: 'home', path: '/home', label: 'My Account' }, // [VP-NOTIF-BADGE 2026-05-28] renamed from 'Home'
  { pageId: 'quotes', path: '/quotes', label: 'Quotes' },
  { pageId: 'customers', path: '/customers', label: 'Customers' },
  { pageId: 'orders', path: '/orders', label: 'Orders' },

  // Production section
  /*
  { pageId: 'production', path: '/production', label: 'Production' },
  { pageId: 'productiondata', path: '/productiondata', label: 'Production Data' },
  */

  // Warehouse section
  /*
  { pageId: 'warehouse', path: '/warehouse', label: 'Warehouse' },
  { pageId: 'warehouse', path: '/suppliers', label: 'Suppliers' },
  { pageId: 'warehouse', path: '/inventory', label: 'Inventory' },
  { pageId: 'warehouse', path: '/materials', label: 'Materials' },
  */
  { pageId: 'setup', path: '/Setup', label: 'PFAP Setup' },
  { pageId: 'statistics', path: '/Statistics', label: 'Statistics' },
  { pageId: 'reports', path: '/reports', label: 'Reports' },
  { pageId: 'templates', path: '/Templates', label: 'Templates' },
  { pageId: 'settings', path: '/Settings', label: 'Settings' },
  // CRM Admin is always visible when user has the page permission — it's how you configure CRM
  { pageId: 'crm-admin', path: '/crm-admin', label: 'CRM Admin' },
  { pageId: 'dealers', path: '/dealers', label: 'Dealers' },
];

const CRM_NAV_LINKS = [
  { pageId: 'quotes', path: '/crm-dashboard', label: 'CRM', requiresCrmRole: true },
  { pageId: 'quotes', path: '/pipeline', label: 'Pipeline', requiresCrmRole: true },
  { pageId: 'quotes', path: '/leads', label: 'Leads', requiresCrmRole: true, requiresCrmManager: true },
];

const Navigation = ({ isAuthenticated, userRole, userPages = [], onLogout, onToggleCollapse, isCollapsed }) => {
  const { companyData, logoUrl } = useCompany();
  const { settings: crmSettings } = useCrmSettings();
  // [VP-NOTIF-BADGE 2026-05-28] global unread-notifications count for the My Account badge.
  const { count: unreadCount } = useUnreadNotifications();
  const navigate = useNavigate();

  if (!isAuthenticated) {
    return null;
  }

  const handleToggle = (e) => {
    e.stopPropagation();
    if (onToggleCollapse) {
      onToggleCollapse(prev => !prev);
    }
  };

  // Check if user has access to a page
  const hasAccess = (pageId) => {
    if (userPages && userPages.length > 0) {
      return userPages.includes(pageId);
    }
    return false;
  };

  const crmRole = Cookies.get('crm_role') || '';
  const allLinks = [
    ...BASE_NAV_LINKS,
    ...(crmSettings.simple_crm_enabled ? CRM_NAV_LINKS : []),
  ];

  // Filter links based on user access
  const visibleLinks = allLinks.filter(link => {
    if (!hasAccess(link.pageId)) return false;
    if (link.requiresCrmRole && !crmRole) return false;
    if (link.requiresCrmManager && crmRole !== 'manager') return false;
    return true;
  });

  return (
    <nav className={`navigation-container ${isCollapsed ? 'collapsed' : ''}`}>
      <button
        type="button"
        className="nav-toggle-btn"
        onClick={handleToggle}
        title={isCollapsed ? 'Show Navigation Pane' : 'Hide Navigation Pane'}
      >
        ☰
      </button>

      {!isCollapsed && (
        <>
          <div className="navigation-header">
            <img src={logoUrl} alt={`${companyData.name || companyName} Logo`} />
            <h1>{companyData.name || companyName}</h1>
          </div>

          <ul className="navigation-list">
            {visibleLinks.map((link, index) => (
              <li key={`${link.path}-${index}`}>
                <NavLink to={link.path} className={({ isActive }) => isActive ? 'selected' : ''}>
                  {link.label}
                  {/* [VP-NOTIF-BADGE 2026-05-28] red badge + count beside My Account when unread > 0.
                      Clicking the badge goes to /home#Notifications so the panel auto-expands and scrolls. */}
                  {link.pageId === 'home' && unreadCount > 0 && (
                    <span
                      className="nav-badge"
                      role="button"
                      tabIndex={0}
                      aria-label={`${unreadCount} unread notifications`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate('/home#Notifications');
                      }}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}

            <li>
              <button onClick={onLogout} className="logout-button">
                Logout ({userRole?.split(" ")[0]})
              </button>
            </li>
          </ul>
        </>
      )}
    </nav>
  );
};

export default Navigation;
